import React from 'react'
import { useContent } from '../../contexts/ContentContext'
import SectionWrapper from '../ui/SectionWrapper'
import SectionLabel from '../ui/SectionLabel'
import CtaButton from '../ui/CtaButton'
import RichText from '../../utils/richText'

/**
 * ProcessSection — 3-step pillars (contentKey-driven).
 * Titles support titleParts accents (primary/bold, highlight green, emphasis red).
 */
export default function ProcessSection({ contentKey = 'processSection' }) {
  const content = useContent()
  const data = content[contentKey] || content.processSection || content.pillars
  if (!data?.steps?.length && !data?.items?.length) return null

  const steps = (data.steps || data.items || []).map((s) => ({
    titleParts: s.titleParts || (s.title ? [{ text: s.title }] : null),
    title: s.title || s.titleParts?.map((p) => p.text).join(' ') || '',
    duration: s.duration || s.subline || '',
    body: s.body || '',
  }))

  const cta = data.cta || data.midCta?.cta
  const footnote = data.footnote || data.midCta?.body

  return (
    <SectionWrapper
      id={data.id || (contentKey === 'processSection' ? 'prozess' : contentKey)}
      data-section="ProcessSection"
      data-testid={`process-section-${contentKey}`}
    >
      <div className="max-w-centered-header text-center mx-auto section-padding-sm">
        {data.label ? <SectionLabel text={data.label} /> : null}
        {data.headline ? (
          <h2 className="typography-h2-mobile md:typography-h2 leading-tight text-center heading-body-spacing">
            {data.headline}
          </h2>
        ) : null}
        {footnote ? (
          <p className="typography-body-lg leading-relaxed text-color-text">{footnote}</p>
        ) : null}
      </div>

      <div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 md:gap-12 content-spacing-lg max-w-5xl mx-auto"
        data-testid="process-steps"
      >
        {steps.map((step, i) => (
          <div key={i} className="relative overflow-visible">
            <span
              aria-hidden="true"
              className="process-bg-number absolute font-secondary text-color-primary select-none pointer-events-none leading-none opacity-10"
            >
              {i + 1}
            </span>
            {i < steps.length - 1 && (
              <div className="hidden md:block absolute top-4 left-full w-full h-px bg-color-bg-light -translate-x-6 z-0 pointer-events-none" />
            )}
            <div className="relative z-10 pt-2">
              {step.titleParts ? (
                <h3 className="typography-h3-mobile md:typography-h3 mt-5 mb-2">
                  <RichText parts={step.titleParts} />
                </h3>
              ) : null}
              {step.duration ? <p className="typography-label mb-5">{step.duration}</p> : null}
              {step.body ? (
                <p className="typography-body leading-relaxed text-color-text">{step.body}</p>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {cta ? (
        <div className="text-center mb-8 mt-8">
          {data.midCta?.headline ? (
            <p className="typography-h3-mobile md:typography-h3 text-color-primary mb-3">
              {data.midCta.headline}
            </p>
          ) : null}
          <CtaButton label={cta} variant="primary" />
        </div>
      ) : null}
    </SectionWrapper>
  )
}
